import { Api, getLspReportBppId } from '@/lib/utils'
import { useStore } from '@/store'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/shallow'

type Tab = 'overview' | 'slots'

export const Route = createFileRoute('/_auth/reports')({
    component: RouteComponent,
})

function RouteComponent() {
    const { token, setPage, accountId } = useStore(useShallow(state => ({
        token: state.token,
        setPage: state.setPage,
        accountId: state.selectedAccount.id,
    })))
    const lspReportBppId = getLspReportBppId(accountId)
    // Exact match only — the broader "bigbasket" family (is_bigbasket_account) shouldn't get this.
    const hasSlotsTab = accountId === 'bigbasketgig'

    const [tab, setTab] = useState<Tab>('overview')

    const getBillingInfo = useQuery({
        queryKey: ['billing_info'],
        queryFn: () => Api<{ status: number, embed_link: string }>('/webui/billing_info', "POST", {}, { token }),
        staleTime: Infinity,
        refetchInterval: Infinity,
        refetchOnWindowFocus: false,
        enabled: !lspReportBppId && tab === 'overview'
    })
    const getLspReport = useQuery({
        queryKey: ['lsp_report', token, lspReportBppId],
        queryFn: () => Api<{ status: number, embed_link: string }>('/webui/reports/lsp', 'POST', { bpp_id: lspReportBppId }, { token }),
        staleTime: Infinity,
        refetchInterval: Infinity,
        refetchOnWindowFocus: false,
        enabled: !!lspReportBppId && tab === 'overview',
    })
    const getSlotsReport = useQuery({
        queryKey: ['slots_report', token],
        queryFn: () => Api<{ status: number, embed_link: string }>('/webui/reports/slots', 'POST', {}, { token }),
        staleTime: Infinity,
        refetchInterval: Infinity,
        refetchOnWindowFocus: false,
        enabled: hasSlotsTab && tab === 'slots',
    })

    useEffect(() => {
        setPage('Reports')
    }, [])

    const overviewQuery = lspReportBppId ? getLspReport : getBillingInfo
    const activeQuery = tab === 'slots' ? getSlotsReport : overviewQuery

    return <div className="absolute left-0 right-0 md:top-15 bottom-3 md:px-5 md:py-3 px-2 flex flex-col">
        {hasSlotsTab && (
            <div className="flex gap-1 border-b border-gray-200 shrink-0 px-2 lg:px-3">
                {([['overview', 'Overview'], ['slots', 'Slots']] as const).map(([key, label]) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === key
                            ? 'border-black text-black'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        {label}
                    </button>
                ))}
            </div>
        )}
        <div className="relative flex-1 min-h-0 mt-2 mx-2 lg:mx-5">
            {activeQuery.data?.embed_link ? (
                <div onMouseDown={e => e.stopPropagation()} className="absolute inset-0">
                    <iframe src={activeQuery.data.embed_link} className="w-full h-full" />
                </div>
            ) : activeQuery.isFetching ? (
                <div className="w-full h-full flex justify-center items-center"><Loader2 /></div>
            ) : null}
        </div>
    </div>
}
